import { FormDrawer, FormDrawerContent, Id, TextField, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { contactService } from "../../../shared/api/contactService";
import { createInvitationListService } from "../../../shared/api/invitationListService";
import { EventSingleSelect } from "../../../shared/select/EventSelect";
import { IInvitationListAttachSingleContactFormState } from "../../../shared/types/invitationList";
import { AttendanceSelectField } from "./AttendanceSelectField";

interface Props {
	open: boolean;
	onClose: () => void;
	contactId: Id;
}
export const InvitationListAttachSingleContactDrawer: FC<Props> = ({ contactId, ...props }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const initialValues: IInvitationListAttachSingleContactFormState = {
		event: null,
		personCount: 1,
		attendancePrediction: "will_attend",
	};
	const onSubmit = useCallback(
		async ({ event, ...values }: IInvitationListAttachSingleContactFormState) => {
			if (!event) throw "Missing validation for: event";

			const service = createInvitationListService(event.id);
			const res = await service.attachContacts({
				contactIds: [contactId],
				distributionListId: null,
				...values,
			});

			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("Invitation list"),
					body: t("Invitation list was successfully updated."),
				});
				void queryClient.invalidateQueries({ queryKey: service.indexKey });
				void queryClient.invalidateQueries({ queryKey: contactService.showKey(contactId) });
				return true;
			}
			return false;
		},
		[contactId, queryClient, showToast, t],
	);

	return (
		<FormDrawer
			{...props}
			title={t("Add contact to invitation list")}
			initialValues={initialValues}
			validate={({ event, ...values }: IInvitationListAttachSingleContactFormState) => {
				const errors: { [K in keyof IInvitationListAttachSingleContactFormState]?: any } = {};

				if (!event) {
					return { event: t("Required field") };
				}
				if (!values.personCount) {
					errors.personCount = t("Required field");
				}
				if (values.personCount < 0) {
					errors.personCount = t("The value has to be greater than 0");
				}
				return errors;
			}}
			onSubmit={onSubmit}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<EventSingleSelect name="event" label={t("Event")} required />
					<TextField name="personCount" label={t("Person count")} type="number" />
					<AttendanceSelectField />
				</FormDrawerContent>
			)}
		/>
	);
};
